/**
 * Phase 2 — Section 8B: "Data Sources" tab inside the WebAdmin dashboard.
 * Panel 1: API key configuration (per-source cards). Panel 2: usage & credits.
 * Keys are system-level; backend stores them encrypted in api_key_store.
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { toast } from "sonner";
import {
  Database, KeyRound, Eye, EyeOff, Upload, CheckCircle2, XCircle,
  Trash2, RefreshCw, AlertTriangle, TrendingUp, ExternalLink, ShieldCheck,
} from "lucide-react";

const STATUS_BADGE = {
  connected: { label: "Connected", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  not_configured: { label: "Not Configured", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  error: { label: "Key Expired or Error", cls: "bg-red-100 text-red-800 border-red-200" },
  free_no_key: { label: "Free — No Key Needed", cls: "bg-sky-100 text-sky-800 border-sky-200" },
};

function SourceCard({ source, onChanged }) {
  const { api } = useAuth();
  const [keyVal, setKeyVal] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const badge = STATUS_BADGE[source.status] || STATUS_BADGE.not_configured;

  const applyKey = useCallback(async () => {
    if (!keyVal.trim()) { toast.error("Enter an API key first"); return; }
    setBusy(true);
    try {
      const res = await api.post("/enrichment/data-sources/apply-key", {
        source: source.id, api_key: keyVal.trim(),
      });
      if (res.data.ok) toast.success(res.data.message);
      else toast.error(res.data.message);
      setKeyVal("");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply key");
    } finally { setBusy(false); }
  }, [api, keyVal, source.id, onChanged]);

  const removeKey = useCallback(async () => {
    if (!window.confirm(`Disconnect ${source.name}? Its stored key will be deleted.`)) return;
    setBusy(true);
    try {
      await api.delete(`/enrichment/data-sources/${source.id}`);
      toast.success(`${source.name} disconnected`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove key");
    } finally { setBusy(false); }
  }, [api, source.id, source.name, onChanged]);

  const onFile = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "").trim();
      let key = text;
      if (file.name.endsWith(".json")) {
        try { key = JSON.parse(text).api_key || text; } catch { /* keep raw */ }
      } else {
        key = text.split(/\r?\n/)[0].trim();
      }
      setKeyVal(key);
      toast.success("Key loaded from file — click Apply Key to save");
    };
    reader.readAsText(file);
  }, []);

  return (
    <Card className="border border-slate-200" data-testid={`ds-card-${source.id}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2.5">
            <div className="p-2 rounded-lg bg-slate-100">
              {source.keyless ? <ShieldCheck className="w-5 h-5 text-sky-600" />
                : <KeyRound className="w-5 h-5 text-slate-600" />}
            </div>
            <div>
              <p className="font-semibold text-slate-900 leading-tight">{source.name}</p>
              <p className="text-xs text-slate-400">{source.plan_tier}</p>
            </div>
          </div>
          <Badge className={`${badge.cls} border text-[11px] whitespace-nowrap`} data-testid={`ds-status-${source.id}`}>
            {source.status === "connected" && <CheckCircle2 className="w-3 h-3 mr-1" />}
            {source.status === "error" && <XCircle className="w-3 h-3 mr-1" />}
            {badge.label}
          </Badge>
        </div>

        {source.keyless ? (
          <div className="flex items-center gap-2 text-sm text-emerald-700">
            <CheckCircle2 className="w-4 h-4" /> Always Connected
          </div>
        ) : (
          <div className="space-y-2.5">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={show ? "text" : "password"}
                  placeholder="Paste API key"
                  value={keyVal}
                  onChange={(e) => setKeyVal(e.target.value)}
                  className="pr-9"
                  data-testid={`ds-key-input-${source.id}`}
                />
                <button type="button" onClick={() => setShow((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <input ref={fileRef} type="file" accept=".txt,.json" hidden onChange={onFile} />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}
                data-testid={`ds-upload-${source.id}`}>
                <Upload className="w-4 h-4" />
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={applyKey} disabled={busy}
                data-testid={`ds-apply-${source.id}`}>
                {busy ? <RefreshCw className="w-4 h-4 mr-1 animate-spin" /> : null}
                Apply Key
              </Button>
              {source.status !== "not_configured" && (
                <Button size="sm" variant="ghost" className="text-red-600" onClick={removeKey}
                  disabled={busy} data-testid={`ds-remove-${source.id}`}>
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              )}
            </div>
            {source.test_result && (
              <p className={`text-xs ${source.status === "connected" ? "text-emerald-600" : "text-red-600"}`}>
                {source.test_result}
              </p>
            )}
            {source.last_tested && (
              <p className="text-[11px] text-slate-400">
                Last tested: {new Date(source.last_tested).toLocaleString()}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function DataSourcesTab() {
  const { api } = useAuth();
  const [sources, setSources] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([
        api.get("/enrichment/data-sources"),
        api.get("/enrichment/data-sources/usage"),
      ]);
      setSources(s.data.sources || []);
      setUsage(u.data || null);
    } catch (e) {
      toast.error("Failed to load data sources");
    } finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="py-16 text-center text-slate-400"><RefreshCw className="w-6 h-6 mx-auto animate-spin mb-2" />Loading data sources…</div>;
  }

  const maxTrend = Math.max(1, ...Object.values(usage?.trend || {}).flatMap((m) => Object.values(m)));

  return (
    <div className="space-y-6" data-testid="data-sources-tab">
      <div className="flex items-center gap-2">
        <Database className="w-5 h-5 text-sky-700" />
        <h2 className="text-lg font-semibold text-slate-900">Data Sources</h2>
      </div>

      {/* Keys-active at-a-glance summary (keyed / paid sources) */}
      {(() => {
        const keyed = sources.filter((s) => !s.keyless);
        const active = keyed.filter((s) => s.status === "connected").length;
        return (
          <Card className="border border-slate-200" data-testid="ds-keys-summary">
            <CardContent className="p-4 flex flex-wrap items-center gap-x-4 gap-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                <ShieldCheck className="w-4 h-4 text-sky-700" />
                Keyed sources active: <span className="text-emerald-700 font-semibold" data-testid="ds-keys-active-count">{active}/{keyed.length}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {keyed.map((s) => {
                  const b = STATUS_BADGE[s.status] || STATUS_BADGE.not_configured;
                  return (
                    <Badge key={s.id} className={`${b.cls} border text-[11px] whitespace-nowrap`} data-testid={`ds-summary-${s.id}`}>
                      {s.status === "connected" && <CheckCircle2 className="w-3 h-3 mr-1" />}
                      {s.status === "error" && <XCircle className="w-3 h-3 mr-1" />}
                      {s.status === "not_configured" && <KeyRound className="w-3 h-3 mr-1" />}
                      {s.name}: {b.label}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Upgrade alerts */}
      {usage?.alerts?.length > 0 && (
        <div className="space-y-2" data-testid="ds-upgrade-alerts">
          {usage.alerts.map((a, i) => (
            <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-orange-50 border border-orange-200">
              <AlertTriangle className="w-5 h-5 text-orange-600 mt-0.5 shrink-0" />
              <div className="text-sm text-orange-900">
                {a.message}{" "}
                {a.upgrade_url && (
                  <a href={a.upgrade_url} target="_blank" rel="noreferrer"
                     className="underline font-medium inline-flex items-center gap-1">
                    Upgrade <ExternalLink className="w-3 h-3" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Panel 1 — API Key Configuration */}
        <div className="space-y-3">
          <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <KeyRound className="w-4 h-4" /> API Key Configuration
          </p>
          {sources.map((s) => <SourceCard key={s.id} source={s} onChanged={load} />)}
        </div>

        {/* Panel 2 — Usage & Credits */}
        <div className="space-y-3">
          <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Usage &amp; Credits
          </p>
          <Card className="border border-slate-200">
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm" data-testid="ds-usage-table">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-100">
                    <th className="py-2.5 px-3">Source</th>
                    <th className="px-3">This Mo.</th>
                    <th className="px-3">Total</th>
                    <th className="px-3">Remaining</th>
                  </tr>
                </thead>
                <tbody>
                  {(usage?.table || []).map((r) => (
                    <tr key={r.id} className="border-b border-slate-50">
                      <td className="py-2.5 px-3 font-medium text-slate-800">{r.source}</td>
                      <td className="px-3 text-slate-600">{r.calls_this_month}</td>
                      <td className="px-3 text-slate-600">{r.calls_total}</td>
                      <td className={`px-3 ${r.near_limit ? "text-orange-600 font-semibold" : "text-slate-600"}`}>
                        {r.remaining_estimate}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {/* 6-month trend */}
          <Card className="border border-slate-200">
            <CardHeader className="pb-2"><CardTitle className="text-sm">Monthly Usage Trend (6 mo)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {(usage?.table || []).filter((r) => !["sec_edgar", "propublica"].includes(r.id) || r.calls_total > 0).map((r) => (
                <div key={r.id}>
                  <p className="text-xs text-slate-500 mb-1">{r.source}</p>
                  <div className="flex items-end gap-1 h-12">
                    {(usage?.months || []).map((mo) => {
                      const v = usage.trend?.[r.id]?.[mo] || 0;
                      return (
                        <div key={mo} className="flex-1 flex flex-col items-center justify-end" title={`${mo}: ${v}`}>
                          <div className="w-full bg-sky-500/80 rounded-t"
                               style={{ height: `${(v / maxTrend) * 100}%`, minHeight: v > 0 ? 3 : 0 }} />
                          <span className="text-[9px] text-slate-400 mt-0.5">{mo.slice(5)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
